import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Avatar,
  Rating,
  LinearProgress,
  Divider,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack,
  Star,
  StarBorder,
  ThumbUp,
  ThumbDown,
  Reply,
  TrendingUp,
  FormatQuote,
  LocalShipping,
  Chat,
  Inventory,
  AttachMoney
} from '@mui/icons-material';
import api from '../services/api';
import { brand, status as statusColors, text, bg, border, shadow } from '../theme/colors';

const RATING_CATEGORIES = [
  { key: 'product_quality_rating', label: 'Product Quality', icon: <Inventory sx={{ fontSize: 18 }} /> },
  { key: 'delivery_rating', label: 'Delivery', icon: <LocalShipping sx={{ fontSize: 18 }} /> },
  { key: 'communication_rating', label: 'Communication', icon: <Chat sx={{ fontSize: 18 }} /> },
  { key: 'value_rating', label: 'Value for Money', icon: <AttachMoney sx={{ fontSize: 18 }} /> }
];

const getRatingColor = (rating) => {
  if (rating >= 4.5) return statusColors.success;
  if (rating >= 3.5) return brand.primary;
  if (rating >= 2.5) return statusColors.warning;
  return statusColors.error;
};

const MerchantReviewsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);

  const handleSubmitReply = async () => {
    if (!selectedReview || !replyText.trim()) return;
    try {
      setReplySubmitting(true);
      const res = await api.post(`/merchants/reviews/${selectedReview.id}/reply`, {
        response: replyText.trim()
      });
      if (res.data.success) {
        // Reflect the reply locally without a full reload
        setReviews((prev) => prev.map((r) =>
          r.id === selectedReview.id
            ? { ...r, merchant_response: res.data.data.merchant_response, merchant_response_date: res.data.data.merchant_response_date }
            : r
        ));
        setReplyDialogOpen(false);
        setSelectedReview(null);
        setReplyText('');
      } else {
        setError(res.data.message || 'Failed to post reply');
      }
    } catch (err) {
      console.error('Error posting reply:', err);
      setError(err.response?.data?.message || 'Failed to post reply');
    } finally {
      setReplySubmitting(false);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Load merchant profile for ID and stats
      const profileRes = await api.get('/merchants/profile/me');
      if (profileRes.data.success) {
        setProfile(profileRes.data.data);

        // Load reviews using profile ID
        const reviewsRes = await api.get(`/merchants/${profileRes.data.data.id}/reviews`);
        if (reviewsRes.data.success) {
          setReviews(reviewsRes.data.data || []);
        }
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Please complete your merchant profile first.');
      } else {
        console.error('Error loading reviews:', err);
        setError('Failed to load reviews');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Calculate rating distribution
  const ratingDistribution = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => Math.round(r.rating) === star).length,
    percent: reviews.length > 0
      ? Math.round((reviews.filter(r => Math.round(r.rating) === star).length / reviews.length) * 100)
      : 0
  }));

  // Calculate category averages
  const categoryAverages = RATING_CATEGORIES.map(cat => {
    const rated = reviews.filter(r => r[cat.key] != null);
    const avg = rated.length > 0
      ? rated.reduce((sum, r) => sum + parseFloat(r[cat.key]), 0) / rated.length
      : 0;
    return { ...cat, average: avg, count: rated.length };
  });

  const overallRating = profile?.rating_average ? parseFloat(profile.rating_average) : 0;
  const totalReviews = profile?.total_ratings || reviews.length;
  const recommendPercent = reviews.length > 0
    ? Math.round((reviews.filter(r => r.would_recommend).length / reviews.length) * 100)
    : 0;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: brand.primary }} />
          <Typography variant="body1" sx={{ mt: 2, color: text.secondary }}>Loading reviews...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/dashboard')} sx={{ color: text.secondary }}>
          <ArrowBack />
        </IconButton>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: text.primary, letterSpacing: '-0.02em' }}>
            Customer Reviews
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, mt: 0.5 }}>
            See what your customers are saying about your business
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Rating Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Overall Rating */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${border.light}`,
              borderRadius: 3,
              textAlign: 'center',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="h1" sx={{
              fontWeight: 800,
              color: getRatingColor(overallRating),
              lineHeight: 1,
              letterSpacing: '-0.04em',
              fontSize: '4rem'
            }}>
              {overallRating.toFixed(1)}
            </Typography>
            <Rating
              value={overallRating}
              precision={0.5}
              readOnly
              size="large"
              sx={{ mt: 1, mb: 1, mx: 'auto' }}
            />
            <Typography variant="body2" sx={{ color: text.secondary }}>
              Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}
            </Typography>
            {recommendPercent > 0 && (
              <Chip
                icon={<ThumbUp sx={{ fontSize: 14 }} />}
                label={`${recommendPercent}% recommend`}
                size="small"
                sx={{
                  mt: 1.5,
                  mx: 'auto',
                  bgcolor: statusColors.successBg,
                  color: statusColors.success,
                  fontWeight: 600,
                  '& .MuiChip-icon': { color: statusColors.success }
                }}
              />
            )}
          </Paper>
        </Grid>

        {/* Rating Distribution */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${border.light}`,
              borderRadius: 3,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: text.primary, mb: 2 }}>
              Rating Breakdown
            </Typography>
            {ratingDistribution.map(({ star, count, percent }) => (
              <Box key={star} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, minWidth: 36 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: text.primary }}>{star}</Typography>
                  <Star sx={{ fontSize: 14, color: '#fbbf24' }} />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={percent}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    bgcolor: `${brand.primary}10`,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 4,
                      bgcolor: star >= 4 ? statusColors.success : star >= 3 ? brand.primary : star >= 2 ? statusColors.warning : statusColors.error
                    }
                  }}
                />
                <Typography variant="caption" sx={{ color: text.tertiary, minWidth: 24, textAlign: 'right' }}>
                  {count}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* Category Ratings */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: `1px solid ${border.light}`,
              borderRadius: 3,
              height: '100%'
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: text.primary, mb: 2 }}>
              Category Scores
            </Typography>
            {categoryAverages.map((cat) => (
              <Box key={cat.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: 1.5,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${brand.primary}10`, color: brand.primary
                  }}>
                    {cat.icon}
                  </Box>
                  <Typography variant="body2" sx={{ color: text.primary, fontWeight: 500 }}>
                    {cat.label}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: getRatingColor(cat.average) }}>
                    {cat.average > 0 ? cat.average.toFixed(1) : '—'}
                  </Typography>
                  <Star sx={{ fontSize: 14, color: cat.average > 0 ? '#fbbf24' : text.disabled }} />
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Reviews List */}
      <Typography variant="h6" sx={{ fontWeight: 600, color: text.primary, mb: 2 }}>
        All Reviews ({reviews.length})
      </Typography>

      {reviews.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            border: `2px dashed ${border.light}`,
            borderRadius: 3,
            bgcolor: bg.subtle
          }}
        >
          <FormatQuote sx={{ fontSize: 56, color: text.tertiary, mb: 1, transform: 'rotate(180deg)' }} />
          <Typography variant="h6" sx={{ color: text.primary, fontWeight: 600, mb: 1 }}>
            No Reviews Yet
          </Typography>
          <Typography variant="body2" sx={{ color: text.secondary, maxWidth: 400, mx: 'auto' }}>
            Reviews from your clients will appear here after they complete orders. Deliver excellent service to earn great feedback!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {reviews.map((review) => (
            <Card
              key={review.id}
              elevation={0}
              sx={{
                border: `1px solid ${border.light}`,
                borderRadius: 3,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: shadow.sm }
              }}
            >
              <CardContent sx={{ p: 3 }}>
                {/* Review header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Avatar sx={{
                      width: 44, height: 44,
                      bgcolor: `${brand.primary}20`,
                      color: brand.primary,
                      fontWeight: 700,
                      fontSize: '1rem'
                    }}>
                      {(review.reviewer_name || 'C')[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: text.primary }}>
                        {review.reviewer_name || 'Customer'}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={review.rating} precision={0.5} readOnly size="small" />
                        <Typography variant="caption" sx={{ color: text.tertiary }}>
                          {new Date(review.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {review.is_verified_review && (
                      <Chip
                        label="Verified Purchase"
                        size="small"
                        sx={{ bgcolor: statusColors.successBg, color: statusColors.success, fontWeight: 600, fontSize: '0.7rem' }}
                      />
                    )}
                    {review.would_recommend && (
                      <Chip
                        icon={<ThumbUp sx={{ fontSize: 12 }} />}
                        label="Recommends"
                        size="small"
                        sx={{
                          bgcolor: `${brand.primary}10`,
                          color: brand.primary,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          '& .MuiChip-icon': { color: brand.primary }
                        }}
                      />
                    )}
                  </Box>
                </Box>

                {/* Review title & text */}
                {review.review_title && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: text.primary, mb: 0.5 }}>
                    {review.review_title}
                  </Typography>
                )}
                {review.review_text && (
                  <Typography variant="body2" sx={{ color: text.secondary, lineHeight: 1.7, mb: 2 }}>
                    {review.review_text}
                  </Typography>
                )}

                {/* Detailed ratings */}
                {(review.product_quality_rating || review.delivery_rating || review.communication_rating || review.value_rating) && (
                  <Box sx={{
                    display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2,
                    p: 1.5, borderRadius: 2, bgcolor: bg.subtle
                  }}>
                    {RATING_CATEGORIES.map(cat => {
                      const val = review[cat.key];
                      if (val == null) return null;
                      return (
                        <Box key={cat.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {cat.icon}
                          <Typography variant="caption" sx={{ color: text.secondary }}>{cat.label}:</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: getRatingColor(val) }}>{parseFloat(val).toFixed(1)}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Merchant response */}
                {review.merchant_response && (
                  <Box sx={{
                    mt: 2, p: 2, borderRadius: 2,
                    bgcolor: `${brand.primary}06`,
                    borderLeft: `3px solid ${brand.primary}`
                  }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: brand.primary, display: 'block', mb: 0.5 }}>
                      Your Response
                    </Typography>
                    <Typography variant="body2" sx={{ color: text.secondary }}>
                      {review.merchant_response}
                    </Typography>
                    {review.merchant_response_date && (
                      <Typography variant="caption" sx={{ color: text.tertiary, mt: 0.5, display: 'block' }}>
                        Replied on {new Date(review.merchant_response_date).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Reply action (if no response yet) */}
                {!review.merchant_response && (
                  <Box sx={{ mt: 1.5 }}>
                    <Button
                      size="small"
                      startIcon={<Reply />}
                      onClick={() => {
                        setSelectedReview(review);
                        setReplyText('');
                        setReplyDialogOpen(true);
                      }}
                      sx={{ color: brand.primary, fontWeight: 600 }}
                    >
                      Reply to Review
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Reply to Review</DialogTitle>
        <DialogContent>
          {selectedReview && (
            <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: bg.subtle }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {selectedReview.reviewer_name || 'Customer'}
                </Typography>
                <Rating value={selectedReview.rating} precision={0.5} readOnly size="small" />
              </Box>
              <Typography variant="body2" sx={{ color: text.secondary }}>
                {selectedReview.review_text || 'No comment'}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Your Response"
            placeholder="Thank your customer and address their feedback professionally..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            inputProps={{ maxLength: 1000 }}
            helperText={`${replyText.length}/1000 characters`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setReplyDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!replyText.trim() || replySubmitting}
            startIcon={<Reply />}
            onClick={handleSubmitReply}
            sx={{
              background: brand.gradient,
              fontWeight: 600,
              '&:hover': { background: brand.gradientHover }
            }}
          >
            Submit Reply
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MerchantReviewsPage;
